/**
 * M6 — Staff Management (5 fixed roles)
 * ======================================
 *
 * Super-admin-only CRUD page for ops staff. Built on the existing
 * `admin_users` collection. Other admin roles can read the list (for
 * the typeahead in Assign-to) but cannot mutate.
 *
 * Theme matches the rest of the admin panel: #1B375B navy / #659A9B teal.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Users, Plus, Search, Loader2, ShieldCheck, ShieldAlert, UserX,
  KeyRound, X, CheckCircle, AlertTriangle,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/staff`;

const ROLE_TONE = {
  super_admin:   { bg: 'bg-rose-100',    text: 'text-rose-900',    label: 'Super Admin' },
  ops_manager:   { bg: 'bg-[#1B375B]',   text: 'text-white',       label: 'Ops Manager' },
  ops_executive: { bg: 'bg-sky-100',     text: 'text-sky-900',     label: 'Ops Executive' },
  viewer:        { bg: 'bg-gray-100',    text: 'text-gray-700',    label: 'Viewer' },
  accounts:      { bg: 'bg-emerald-100', text: 'text-emerald-900', label: 'Accounts' },
};

function RoleBadge({ role }) {
  const t = ROLE_TONE[role] || ROLE_TONE.viewer;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${t.bg} ${t.text}`}
      data-testid={`staff-role-badge-${role}`}
    >
      {t.label}
    </span>
  );
}

function Toast({ kind, text, onDismiss }) {
  if (!text) return null;
  const tone = kind === 'ok'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  const Icon = kind === 'ok' ? CheckCircle : AlertTriangle;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${tone}`} data-testid="staff-toast">
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1">{text}</div>
      <button onClick={onDismiss} className="hover:opacity-70">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Create / Edit modal ───────────────────────────────────────────
function StaffModal({ open, mode, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: '', name: '', role: 'viewer', password: '', phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setErr(null);
      setBusy(false);
      setForm({
        email:    initial?.email || '',
        name:     initial?.name  || '',
        role:     initial?.role  || 'viewer',
        password: '',
        phone:    initial?.phone || '',
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'create') {
        await axios.post(API, form, { withCredentials: true });
      } else {
        const patch = {};
        if (form.name  !== initial.name)  patch.name  = form.name;
        if (form.role  !== initial.role)  patch.role  = form.role;
        if (form.phone !== (initial.phone || '')) patch.phone = form.phone;
        if (Object.keys(patch).length === 0) {
          setBusy(false);
          onClose();
          return;
        }
        await axios.patch(`${API}/${initial.id}`, patch, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" data-testid="staff-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-[#1B375B] mb-4">
          {mode === 'create' ? 'Add new staff' : 'Edit staff'}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500">Email</label>
            <input
              type="email"
              value={form.email}
              disabled={mode === 'edit'}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-50"
              data-testid="staff-form-email"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="staff-form-name"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="staff-form-role"
            >
              {Object.keys(ROLE_TONE).map((r) => (
                <option key={r} value={r}>{ROLE_TONE[r].label}</option>
              ))}
            </select>
          </div>
          {mode === 'create' && (
            <div>
              <label className="text-[10px] uppercase font-semibold text-gray-500">Initial password (min 8)</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                data-testid="staff-form-password"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase font-semibold text-gray-500">Phone (optional)</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="staff-form-phone"
            />
          </div>
          {err && <p className="text-sm text-rose-700">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg" data-testid="staff-modal-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.email || !form.name || (mode === 'create' && !form.password)}
            className="px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg hover:bg-[#0f2742] disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="staff-modal-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === 'create' ? 'Create staff' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────────────────
export default function StaffManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [me, setMe] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: 'create', initial: null });
  const [toast, setToast] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [staff, me] = await Promise.all([
        axios.get(API, { params: { active_only: !showInactive }, withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/me`, { withCredentials: true }),
      ]);
      setRows(staff.data.rows || []);
      setMe(me.data);
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed to load staff' });
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.role || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const isSuper = me?.role === 'super_admin';

  const disable = async (row) => {
    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!confirm(`Disable ${row.name} (${row.email})?`)) return;
    try {
      await axios.delete(`${API}/${row.id}`, { withCredentials: true });
      setToast({ kind: 'ok', text: `${row.name} disabled` });
      fetchRows();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Disable failed' });
    }
  };

  const resetPassword = async (row) => {
    // eslint-disable-next-line no-alert
    const np = window.prompt(`Reset password for ${row.email}\nNew password (min 8 chars):`);
    if (!np || np.length < 8) {
      if (np) setToast({ kind: 'err', text: 'Password too short (min 8)' });
      return;
    }
    try {
      await axios.post(`${API}/${row.id}/reset-password`, { new_password: np }, { withCredentials: true });
      setToast({ kind: 'ok', text: `Password reset for ${row.email}` });
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Reset failed' });
    }
  };

  return (
    <div className="px-4 py-5" data-testid="staff-management">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-6 h-6 text-[#659A9B]" />
            Staff Management
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage ops team — super_admin · ops_manager · ops_executive · viewer · accounts
            {me ? <span className="ml-2 inline-flex items-center gap-1">· You are <RoleBadge role={me.role} /></span> : null}
          </p>
        </div>
        {isSuper && (
          <button
            onClick={() => setModal({ open: true, mode: 'create', initial: null })}
            data-testid="staff-add-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1B375B] text-white text-sm font-semibold rounded-lg hover:bg-[#0f2742]"
          >
            <Plus className="w-4 h-4" />
            Add staff
          </button>
        )}
      </header>

      {toast && (
        <div className="mb-3">
          <Toast {...toast} onDismiss={() => setToast(null)} />
        </div>
      )}

      {!isSuper && (
        <div className="mb-3 p-3 rounded-lg border border-sky-200 bg-sky-50 text-xs text-sky-900">
          <ShieldAlert className="w-4 h-4 inline mr-1" />
          Read-only — only super_admin can create/edit/disable staff.
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name / email / role"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg"
              data-testid="staff-search"
            />
          </div>
          <label className="inline-flex items-center gap-1 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="staff-show-inactive"
            />
            Show inactive
          </label>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400 ml-auto" />}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="staff-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Name · Email</th>
              <th className="text-left px-3 py-2.5 font-semibold">Role</th>
              <th className="text-left px-3 py-2.5 font-semibold">Phone</th>
              <th className="text-left px-3 py-2.5 font-semibold">Last Login</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status</th>
              <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                {loading ? 'Loading…' : 'No staff match.'}
              </td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`staff-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <p className="font-semibold text-gray-800">{r.name}</p>
                  <p className="text-[10px] text-gray-500">{r.email}</p>
                </td>
                <td className="px-3 py-2.5"><RoleBadge role={r.role} /></td>
                <td className="px-3 py-2.5 text-xs text-gray-600">{r.phone || '—'}</td>
                <td className="px-3 py-2.5 text-xs text-gray-600">
                  {r.last_login_at ? new Date(r.last_login_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                </td>
                <td className="px-3 py-2.5">
                  {r.is_active === false
                    ? <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">Disabled</span>
                    : <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Active</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {isSuper && (
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setModal({ open: true, mode: 'edit', initial: r })}
                        className="px-2 py-1 text-[11px] font-semibold text-[#1B375B] hover:bg-gray-100 rounded"
                        data-testid={`staff-edit-${r.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => resetPassword(r)}
                        className="px-2 py-1 text-[11px] font-semibold text-amber-700 hover:bg-amber-50 rounded inline-flex items-center gap-1"
                        data-testid={`staff-reset-${r.id}`}
                      >
                        <KeyRound className="w-3 h-3" /> Reset
                      </button>
                      {r.is_active !== false && r.id !== me?.id && (
                        <button
                          onClick={() => disable(r)}
                          className="px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 rounded inline-flex items-center gap-1"
                          data-testid={`staff-disable-${r.id}`}
                        >
                          <UserX className="w-3 h-3" /> Disable
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StaffModal
        open={modal.open}
        mode={modal.mode}
        initial={modal.initial}
        onClose={() => setModal({ open: false, mode: 'create', initial: null })}
        onSaved={() => { setModal({ open: false, mode: 'create', initial: null }); fetchRows(); setToast({ kind: 'ok', text: 'Saved' }); }}
      />
    </div>
  );
}
